/**
 * The editor store (plan D1): one typed observable holding `EditorState`, mutated only through
 * `actions`, observed through `subscribe` / `select`.
 *
 * Framework-free and synchronous: no DOM, no three.js, no persistence, no async. Listeners fire
 * synchronously after each action; `batch(fn)` collapses a group of actions into one
 * notification. Updates are immutable — only the objects on the changed path are replaced, so
 * UI code can compare slices with `===`.
 */

import { CANONICAL_BONE_IDS, getKeyframeAt, lastKeyframeFrame } from '../shared';
import type {
  CanonicalAnimationClip,
  CanonicalBoneId,
  Pose,
  RotationKeyframe,
  TweenPreset,
} from '../shared';
import { createActions, evaluatedPose, type Actions } from './actions';
import type { ActionContext } from './actions/context';
import { createInitialState, type EditorMode, type EditorState } from './types';
import { createUndoStack, type UndoStack } from './undo';

export type StoreListener = (state: EditorState, previous: EditorState) => void;
export type Unsubscribe = () => void;

export interface Store {
  getState(): EditorState;
  subscribe(listener: StoreListener): Unsubscribe;
  /**
   * Subscribe to one slice. The listener runs only when `selector(state)` changes according to
   * `equals` (default `Object.is`). It is NOT called for the initial value.
   */
  select<T>(
    selector: (state: EditorState) => T,
    listener: (value: T, previous: T) => void,
    equals?: (a: T, b: T) => boolean,
  ): Unsubscribe;
  /** Run several actions and notify subscribers once, at the end. */
  batch(run: () => void): void;
  actions: Actions;
  history: UndoStack;
}

export function createStore(initial?: Partial<EditorState>): Store {
  let state = createInitialState(initial);
  let notified = state;
  let depth = 0;
  let toastId = 0;
  const listeners = new Set<StoreListener>();
  const history = createUndoStack();

  function notify(): void {
    if (depth > 0 || state === notified) return;
    const previous = notified;
    notified = state;
    for (const listener of [...listeners]) listener(state, previous);
  }

  function set(patch: Partial<EditorState>): void {
    const current = state as unknown as Record<string, unknown>;
    const incoming = patch as unknown as Record<string, unknown>;
    let changed = false;
    for (const key of Object.keys(incoming)) {
      if (!Object.is(current[key], incoming[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    state = { ...state, ...patch };
    notify();
  }

  function batch(run: () => void): void {
    depth++;
    try {
      run();
    } finally {
      depth--;
    }
    notify();
  }

  /** Mirror the undo stack into `state.history` so the UI can bind to it like any other slice. */
  function syncHistory(): void {
    const current = state.history;
    if (
      current.canUndo === history.canUndo &&
      current.canRedo === history.canRedo &&
      current.undoLabel === history.undoLabel &&
      current.redoLabel === history.redoLabel
    ) {
      return;
    }
    set({
      history: {
        canUndo: history.canUndo,
        canRedo: history.canRedo,
        undoLabel: history.undoLabel,
        redoLabel: history.redoLabel,
      },
    });
  }

  const ctx: ActionContext = {
    getState: () => state,
    set,
    history,
    nextToastId: () => ++toastId,
  };

  // One notification and one history sync per dispatched action, however many `set` calls it made.
  const raw = createActions(ctx) as unknown as Record<string, (...args: unknown[]) => unknown>;
  const wrapped: Record<string, unknown> = {};
  for (const name of Object.keys(raw)) {
    const fn = raw[name];
    wrapped[name] = (...args: unknown[]): unknown => {
      let result: unknown;
      batch(() => {
        result = fn(...args);
        syncHistory();
      });
      return result;
    };
  }

  return {
    getState: () => state,

    subscribe(listener: StoreListener): Unsubscribe {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    select<T>(
      selector: (s: EditorState) => T,
      listener: (value: T, previous: T) => void,
      equals: (a: T, b: T) => boolean = Object.is,
    ): Unsubscribe {
      let last = selector(state);
      const wrapper: StoreListener = (next) => {
        const value = selector(next);
        if (equals(value, last)) return;
        const previous = last;
        last = value;
        listener(value, previous);
      };
      listeners.add(wrapper);
      return () => {
        listeners.delete(wrapper);
      };
    },

    batch,
    actions: wrapped as unknown as Actions,
    history,
  };
}

// ---------------------------------------------------------------------------
// Selectors — pure reads over `EditorState`, shared by every UI module.
// ---------------------------------------------------------------------------

export function selectMode(state: EditorState): EditorMode {
  return state.mode;
}

export function selectClip(state: EditorState): CanonicalAnimationClip | null {
  return state.clip;
}

export function selectSelectedBone(state: EditorState): CanonicalBoneId | null {
  return state.selection.bone;
}

export function selectPlayheadFrame(state: EditorState): number {
  return state.playhead.frame;
}

export function selectIsPlaying(state: EditorState): boolean {
  return state.playhead.playing;
}

/** The pose the viewport should render: the working pose (already evaluated when scrubbing). */
export function selectDisplayPose(state: EditorState): Pose {
  return state.livePose;
}

/** The pose the clip alone produces at `frame` (defaults to the playhead). */
export function selectEvaluatedPose(state: EditorState, frame?: number): Pose {
  return evaluatedPose(state, frame);
}

/** Bones that have a track, in canonical hierarchy order. */
export function selectKeyedBones(state: EditorState): CanonicalBoneId[] {
  if (!state.clip) return [];
  const clip = state.clip;
  return CANONICAL_BONE_IDS.filter((bone) => {
    const track = clip.tracks[bone];
    return !!track && track.keyframes.length > 0;
  });
}

/** Rows the dope sheet should draw, honouring `timeline.visibleBones`. */
export function selectVisibleBones(state: EditorState): CanonicalBoneId[] {
  switch (state.timeline.visibleBones) {
    case 'keyed':
      return selectKeyedBones(state);
    case 'selected':
      return state.selection.bone ? [state.selection.bone] : [];
    case 'all':
    default:
      return [...CANONICAL_BONE_IDS];
  }
}

export function selectBoneKeyframes(
  state: EditorState,
  bone: CanonicalBoneId,
): readonly RotationKeyframe[] {
  return state.clip?.tracks[bone]?.keyframes ?? [];
}

export function isKeySelected(state: EditorState, bone: CanonicalBoneId, frame: number): boolean {
  return state.keySelection.some((ref) => ref.bone === bone && ref.frame === frame);
}

export function hasKeyAt(state: EditorState, bone: CanonicalBoneId, frame: number): boolean {
  return state.clip ? getKeyframeAt(state.clip, bone, frame) !== undefined : false;
}

/** Easing shared by the whole key selection, or `null` when it is mixed/empty. */
export function selectSelectionEasing(state: EditorState): TweenPreset | null {
  if (!state.clip || state.keySelection.length === 0) return null;
  let easing: TweenPreset | null = null;
  for (const ref of state.keySelection) {
    const key = getKeyframeAt(state.clip, ref.bone, ref.frame);
    if (!key) continue;
    if (easing === null) easing = key.easing;
    else if (easing !== key.easing) return null;
  }
  return easing;
}

export function selectLastKeyframeFrame(state: EditorState): number {
  return state.clip ? lastKeyframeFrame(state.clip) : 0;
}

export function selectDurationFrames(state: EditorState): number {
  return state.clip?.durationFrames ?? 0;
}

export function selectIsClipDirty(state: EditorState): boolean {
  return state.clipDirty;
}

export function selectCanUndo(state: EditorState): boolean {
  return state.history.canUndo;
}

export function selectCanRedo(state: EditorState): boolean {
  return state.history.canRedo;
}
